<script setup lang="ts">
import { computed, ref, onUnmounted } from "vue";
import { router } from "@inertiajs/vue3";
import axios from "axios";
import { toast } from "vue-sonner";
import {
    apiPathUrl,
    isCurrentUserById,
    isGuest,
    isLoggedIn,
    isVerified,
    mustBeLogin,
    mustBeVerified,
} from "@/Utils/helpers";
import { useModal } from "@/Utils/use-modal";
import TheLayoutApp from "@/Layouts/TheLayoutApp.vue";
import AppContainer from "@/Components/Shared/container/AppContainer.vue";
import AppPage from "@/Components/Shared/page/AppPage.vue";
import AppHeader from "@/Components/Shared/header/AppHeader.vue";
import AppTitle from "@/Components/Shared/title/AppTitle.vue";
import AppPlaceholder from "@/Components/Shared/placeholder/AppPlaceholder.vue";
import AppButton from "@/Components/Shared/button/AppButton.vue";
import AppArticle from "@/Components/Shared/article/AppArticle.vue";
import TheCollectionForm from "@/Components/App/collections/TheCollectionForm.vue";
import AppButtonGroup from "@/Components/Shared/button/AppButtonGroup.vue";
import AppConfirmationDelete from "@/Components/Shared/dialog/AppConfirmationDelete.vue";
import TheFlagForm from "@/Components/App/flags/TheFlagForm.vue";
import AppCollectionProducts from "@/Components/App/collections/AppCollectionProducts.vue";
import AppCollectionProductsDraggable from "@/Components/App/collections/AppCollectionProductsDraggable.vue";
import AppCollectionManagement from "@/Components/App/collections/AppCollectionManagement.vue";
import TheAddProductToCollectionForm from "@/Components/App/collections/TheAddProductToCollectionForm.vue";
import AppUserAvatar from "@/Components/Shared/user/AppUserAvatar.vue";
import { useCollectionSorting } from "@/Composables/useCollectionSorting";

type Props = {
    collection: object;
    products: object;
};

const { collection, products } = defineProps<Props>();

const isManaging = ref(false);
const {
    isSaving,
    saveOrder,
    toggleOrdered: toggleOrderedApi,
    cleanup,
} = useCollectionSorting(collection.data.id);

const isOwner = computed(() => {
    return isLoggedIn.value
        ? isCurrentUserById(collection.data.author.id)
        : false;
});

const { openModal } = useModal();

const addProduct = () => {
    if (isGuest.value) {
        mustBeLogin();
        return;
    }

    if (!isVerified.value) {
        mustBeVerified();
        return;
    }

    openModal({
        component: TheAddProductToCollectionForm,
        title: "Product toevoegen aan collectie",
        props: {
            collection: collection.data,
            buttonText: "Toevoegen",
        },
    });
};

const edit = () => {
    if (isGuest.value) {
        mustBeLogin();
        return;
    }
    if (!isVerified.value) {
        mustBeVerified();
        return;
    }
    openModal({
        component: TheCollectionForm,
        title: "Collectie wijzigen",
        props: {
            collection: collection.data,
            buttonText: "Wijzig",
            redirectToCollection: true,
        },
    });
};

const deleteProductFromCollection = (product: any) => {
    if (isGuest.value) {
        mustBeLogin();
        return;
    }

    if (!isVerified.value || !isOwner.value) {
        mustBeVerified();
        return;
    }

    const formData = new FormData();
    formData.append("collection", collection.data.id);
    formData.append("products[]", product.id);

    if (
        confirm(
            "Weet je zeker dat je dit product uit de collectie wilt verwijderen?",
        )
    ) {
        axios
            .post(apiPathUrl(`/collections/detach/`), formData)
            .then(() => {
                toast.success("Het product is verwijderd uit de collectie.");
                router.reload({ preserveScroll: true });
            })
            .catch(() => {
                toast.error(
                    "Er ging iets mis bij het verwijderen van het product",
                );
            });
    }
};

const flag = () => {
    if (isGuest.value) {
        mustBeLogin();
        return;
    }

    if (!isVerified.value) {
        mustBeVerified();
        return;
    }

    openModal({
        component: TheFlagForm,
        title: "Collectie rapporteren",
        props: {
            itemId: collection.data.id,
            apiPath: collection.data._path,
            type: "collectie",
        },
    });
};

const toggleOrdered = async () => {
    if (isGuest.value) {
        mustBeLogin();
        return;
    }

    if (!isVerified.value) {
        mustBeVerified();
        return;
    }

    const newOrderedState = await toggleOrderedApi(!!collection.data.ordered);

    // Reload the page with Inertia to get fresh data
    router.reload({
        preserveScroll: true,
        preserveState: false,
        onSuccess: () => {
            // If we disabled ordering, exit manage mode
            if (!newOrderedState) {
                isManaging.value = false;
            }
        },
    });
};

const handleOrderChanged = movedProduct => {
    saveOrder(movedProduct);
};

const startManaging = () => {
    isManaging.value = true;
};

const stopManaging = () => {
    isManaging.value = false;
};

onUnmounted(() => {
    cleanup();
});

const breadcrumbs = [
    {
        label: "Community",
        path: "/community",
    },
    {
        label: `@${collection.data.author.username}`,
        path: collection.data.author.path,
    },
];
</script>

<template>
    <TheLayoutApp
        :title="`@${collection.data.author.username}'s ${collection.data.name}`"
        content-classes="flex-col"
        :breadcrumbs="breadcrumbs"
    >
        <AppPage
            v-if="products.data"
            size="small"
        >
            <AppContainer>
                <AppUserAvatar
                    :user="collection.data.author"
                    show-name
                    avatar-size="xsmall"
                />
                <AppHeader
                    :size="collection.data.description ? 'small' : 'regular'"
                >
                    <AppTitle size="large">
                        {{ collection.data.name }}
                    </AppTitle>
                </AppHeader>

                <AppArticle
                    v-if="collection.data.description"
                    size="large"
                    class="mb-6"
                >
                    {{ collection.data.description }}
                </AppArticle>

                <div class="space-y-4 mb-8">
                    <AppCollectionManagement
                        v-if="isOwner"
                        :ordered="!!collection.data.ordered"
                        :is-managing="isManaging"
                        @toggle-ordered="toggleOrdered"
                        @start-managing="startManaging"
                        @stop-managing="stopManaging"
                    />

                    <AppButtonGroup>
                        <template v-if="isOwner">
                            <AppButton
                                color="primary"
                                size="xsmall"
                                @click="addProduct"
                            >
                                Aangeraden product toevoegen
                            </AppButton>

                            <AppButton
                                size="xsmall"
                                @click="edit"
                            >
                                Wijzig
                            </AppButton>

                            <AppConfirmationDelete
                                :action="collection.data._path"
                                button-size="xsmall"
                                label="Verwijder"
                                :redirect="`${collection.data.author.path}/collecties`"
                            />
                        </template>
                        <template v-else>
                            <AppButton
                                size="xsmall"
                                @click="flag"
                            >
                                Rapporteren
                            </AppButton>
                        </template>
                    </AppButtonGroup>
                </div>

                <AppCollectionProductsDraggable
                    v-if="isOwner && collection.data.ordered"
                    :products="products.data"
                    :meta="products.meta"
                    :show-close-button="isOwner"
                    :author="collection.data.author.username"
                    :is-owner="isOwner"
                    :is-ordered="!!collection.data.ordered"
                    :is-managing="isManaging"
                    :context="{
                        type: 'collection',
                        id: collection.data.id,
                        subType: collection.data.subType,
                        _path: collection.data._path,
                    }"
                    :via="collection.data.author.id"
                    @order-changed="handleOrderChanged"
                    @delete-product="deleteProductFromCollection"
                />
                <AppCollectionProducts
                    v-else
                    :products="products.data"
                    :meta="products.meta"
                    :show-close-button="isOwner"
                    :author="collection.data.author.username"
                    :is-owner="isOwner"
                    :context="{
                        type: 'collection',
                        id: collection.data.id,
                        _path: collection.data._path,
                    }"
                    :via="collection.data.author.id"
                    :is-ordered="!!collection.data.ordered"
                    @delete-product="deleteProductFromCollection"
                />
            </AppContainer>
        </AppPage>
        <AppPlaceholder v-else>
            Nog geen producten toegevoegd aan deze collectie.
        </AppPlaceholder>
    </TheLayoutApp>
</template>
